import React, { useState, useEffect } from "react";
import Select from "react-select";
import { getUsersSummary } from "../../../../api/api";
import { selectStyles } from "../../../../utils/selectStyles";
import "../Estimate/ApprovalModal/ApprovalModal.css";

/** Собрать строку должностей из пользователя: access_levels и/или roles, через " / " */
const getPositionTitleFromUser = (user) => {
  const parts = [];
  if (user.access_levels?.length) {
    user.access_levels.forEach((l) => {
      const name = typeof l === "string" ? l : l?.name;
      if (name) parts.push(name);
    });
  }
  if (user.roles?.length) {
    user.roles.forEach((r) => {
      const name = typeof r === "string" ? r : r?.name;
      if (name) parts.push(name);
    });
  }
  return [...new Set(parts)].filter(Boolean).join(" / ") || "";
};

const CommercialOfferApprovalModal = ({
  open,
  onClose,
  onSubmit,
  projectData,
  isSubmitting = false,
  title = "Согласование КП",
  warningText = "Подтвердите отправку КП на согласование. После этого ее нельзя будет редактировать.",
  allowAddFromUserList = false,
}) => {
  const [approvers, setApprovers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState(null);

  // Инициализация списка согласующих на основе данных проекта
  useEffect(() => {
    if (projectData) {
      const approversList = [];

      approversList.push({
        id: 41,
        title: "Директор компании",
        name: "Бекбауов Серикпай",
      });

      approversList.push({
        id: 62,
        title: "Финансовый руководитель",
        name: "Тлеубаева Бакыт",
      });

      if (projectData.chief) {
        const chiefName = `${projectData.chief.name || ""} ${
          projectData.chief.surname || ""
        }`.trim();
        approversList.push({
          id: projectData.chief.id || "chief",
          title: "ГИП проекта",
          name: chiefName || "Не назначен",
        });
      }

      setApprovers(approversList);
    }
  }, [projectData]);

  // Загрузка списка пользователей для добавления согласующих (только при allowAddFromUserList)
  useEffect(() => {
    if (!open || !allowAddFromUserList) return;
    const load = async () => {
      setUsersLoading(true);
      try {
        const res = await getUsersSummary({ isActive: true, pageSize: 1000 });
        setUsersList(res?.users || []);
      } catch (e) {
        console.error("Ошибка загрузки списка пользователей:", e);
        setUsersList([]);
      } finally {
        setUsersLoading(false);
      }
    };
    load();
  }, [open, allowAddFromUserList]);

  const addApproverFromUser = (user) => {
    if (!user || approvers.some((a) => a.id === user.id)) return;
    const name = [user.surname, user.name].filter(Boolean).join(" ").trim() || "Без имени";
    const positionTitle = getPositionTitleFromUser(user);
    setApprovers((prev) => [
      ...prev,
      { id: user.id, title: positionTitle || "—", name },
    ]);
    setSelectedUserToAdd(null);
  };

  if (!open) return null;

  const handleRemoveApprover = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setApprovers((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit && onSubmit(approvers.map((a) => a.id));
  };

  return (
    <>
      <div className="approval-modal-backdrop" onClick={onClose} />
      <div className="approval-modal">
        <div className="approval-modal-title">{title}</div>
        <div className="approval-modal-warning">
          <span className="approval-modal-warning-icon">⚠️</span>
          <span>{warningText}</span>
        </div>
        <div className="approval-modal-label">
          Выберите кому отправить на согласование:
        </div>
        <form onSubmit={handleSubmit}>
          <div className="approval-modal-list">
            {approvers.map((a) => (
              <div key={a.id} className="approval-modal-list-row">
                <div className="approval-modal-approver-info">
                  <span className="approval-checkbox-title">{a.title}</span>
                  <span className="approval-checkbox-name">{a.name}</span>
                </div>
                <button
                  type="button"
                  className="approval-modal-remove-btn"
                  onClick={(e) => handleRemoveApprover(e, a.id)}
                  title="Удалить из списка"
                  aria-label="Удалить из списка"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          {allowAddFromUserList && (
            <div className="approval-modal-add-user">
              <div className="approval-modal-add-user-label">
                Добавить согласующего из списка пользователей:
              </div>
              <Select
                isClearable
                placeholder={usersLoading ? "Загрузка..." : "Выберите пользователя"}
                isDisabled={usersLoading}
                options={usersList
                  .filter((u) => !approvers.some((a) => a.id === u.id))
                  .map((u) => ({
                    value: u.id,
                    label: [u.surname, u.name].filter(Boolean).join(" ").trim() || "Без имени",
                    userData: u,
                  }))}
                value={selectedUserToAdd}
                onChange={(opt) => {
                  setSelectedUserToAdd(opt);
                  if (opt?.userData) addApproverFromUser(opt.userData);
                }}
                styles={selectStyles}
                className="approval-modal-user-select"
              />
            </div>
          )}
          <div className="approval-modal-actions">
            <button
              type="button"
              className="approval-btn outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="approval-btn green"
              disabled={isSubmitting || approvers.length === 0}
            >
              {isSubmitting ? "Отправка..." : "Отправить"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CommercialOfferApprovalModal;
